import { useEffect, useRef, type CSSProperties } from "react";
import type { Game } from "@engine/core/Game";

interface Props {
  game?: Game | undefined;
  /** Map size in tiles. */
  worldWidth?: number;
  worldHeight?: number;
  tileSize?: number;
  /** Hex color for the minimap background. */
  bg?: number;
  /** Side length in CSS pixels. */
  size?: number;
}

const wrapStyle = (size: number): CSSProperties => ({
  position: "fixed",
  top: 8,
  right: 300,
  width: size,
  height: size,
  background: "rgba(0,0,0,0.55)",
  border: "1px solid rgba(255,255,255,0.18)",
  borderRadius: 6,
  overflow: "hidden",
  zIndex: 990,
  pointerEvents: "none",
});

const KIND_COLORS: Record<string, string> = {
  player: "#60a5fa",
  remotePlayer: "#a855f7",
  npc: "#facc15",
  monster: "#ef4444",
  audio_emitter: "#4ade80",
  portal: "#f472b6",
};

/**
 * Top-down 2D minimap. Reads ECS positions via `game.inspector` (which already
 * has a per-scene entity provider). Player is centered on the minimap;
 * NPCs / monsters / emitters appear as colored dots.
 *
 * Cheap: a single canvas redrawn at 10 Hz.
 */
export function Minimap({
  game,
  worldWidth = 24,
  worldHeight = 16,
  tileSize = 32,
  bg = 0x0a0e1c,
  size = 160,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (!game) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const worldPxW = worldWidth * tileSize;
    const worldPxH = worldHeight * tileSize;
    const scaleX = size / worldPxW;
    const scaleY = size / worldPxH;
    const bgHex = `#${bg.toString(16).padStart(6, "0")}`;

    let raf = 0;
    let last = 0;
    const tick = (ts: number): void => {
      raf = requestAnimationFrame(tick);
      if (ts - last < 100) return;
      last = ts;

      ctx.fillStyle = bgHex;
      ctx.fillRect(0, 0, size, size);
      ctx.strokeStyle = "rgba(255,255,255,0.12)";
      ctx.strokeRect(0.5, 0.5, size - 1, size - 1);

      // Pull entities through inspector's provider (set by the active scene)
      const provider = (game.inspector as unknown as { provider?: () => Iterable<unknown> }).provider;
      if (typeof provider === "function") {
        for (const e of provider()) {
          const ent = e as { kind?: string; position?: { x: number; y: number }; localControlled?: true };
          if (!ent.position) continue;
          const x = ent.position.x * scaleX;
          const y = ent.position.y * scaleY;
          if (x < -2 || x > size + 2 || y < -2 || y > size + 2) continue;
          const color = ent.localControlled ? "#60a5fa" : (KIND_COLORS[ent.kind ?? ""] ?? "#cccccc");
          ctx.fillStyle = color;
          const r = ent.localControlled ? 3 : 2;
          ctx.beginPath();
          ctx.arc(x, y, r, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      // Camera viewport rectangle
      const cam = game.camera;
      const view = cam.getViewBounds();
      const vx = view.minX * scaleX;
      const vy = view.minY * scaleY;
      const vw = (view.maxX - view.minX) * scaleX;
      const vh = (view.maxY - view.minY) * scaleY;
      ctx.strokeStyle = "rgba(255,255,255,0.45)";
      ctx.lineWidth = 1;
      ctx.strokeRect(vx, vy, vw, vh);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [game, worldWidth, worldHeight, tileSize, bg, size]);

  if (!game) return null;
  return (
    <div style={wrapStyle(size)} data-testid="minimap">
      <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block" }} />
    </div>
  );
}
